import { useState, useEffect, useMemo } from "react"
import { Baby, Milk, GlassWater, Clock, X, CalendarIcon, FileText, DropletIcon, ThermometerIcon } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Slider } from "@/components/ui/slider"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { useQueryClient } from "@tanstack/react-query"
import { useChildStore } from "@/lib/store/child"
import { useCaregiverStore } from "@/lib/store/caregiver"
import { supabase } from "@/integrations/supabase/client"
import { format } from "date-fns"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { Database } from "@/types/supabase"
import { FeedingActivity, FeedingSession } from "@/types/activity"
import { formatDateToUTC } from "@/lib/utils/date"

// Database types
type FeedingTypeEnum = Database["public"]["Enums"]["feeding_type_enum"]
type FeedingSessionInsert = Database["public"]["Tables"]["feeding_sessions"]["Insert"]
type FeedingSessionUpdate = Database["public"]["Tables"]["feeding_sessions"]["Update"]
type BreastfeedingSessionInsert = Database["public"]["Tables"]["breastfeeding_sessions"]["Insert"]
type BreastfeedingSessionUpdate = Database["public"]["Tables"]["breastfeeding_sessions"]["Update"]
type BottleSessionInsert = Database["public"]["Tables"]["bottle_sessions"]["Insert"]
type BottleSessionUpdate = Database["public"]["Tables"]["bottle_sessions"]["Update"]
type FormulaSessionInsert = Database["public"]["Tables"]["formula_sessions"]["Insert"]
type FormulaSessionUpdate = Database["public"]["Tables"]["formula_sessions"]["Update"]

interface FeedingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  childId?: string
  activityToEdit?: FeedingActivity
  onSuccess?: (data: FeedingSession) => void
}

// UI elements
interface FeedingOption {
  id: FeedingTypeEnum
  label: string
  icon: React.ElementType
  description: string
}

const feedingOptions: FeedingOption[] = [
  {
    id: "breastfeeding",
    label: "Breast Feeding",
    icon: Baby,
    description: "Track breastfeeding duration and side"
  },
  {
    id: "bottle",
    label: "Bottle (Breast Milk)",
    icon: Milk,
    description: "Track amount of expressed breast milk"
  },
  {
    id: "formula",
    label: "Formula",
    icon: GlassWater,
    description: "Track amount of formula given"
  }
]

export function FeedingDialog({
  open,
  onOpenChange,
  childId,
  activityToEdit,
  onSuccess
}: FeedingDialogProps) {
  // Step management
  const [currentStep, setCurrentStep] = useState<'type' | 'time' | 'duration' | 'details'>('type')
  
  // Core feeding data
  const [feedingType, setFeedingType] = useState<FeedingTypeEnum>("breastfeeding")
  const [startDateTime, setStartDateTime] = useState<Date>(new Date())
  const [startTimeInput, setStartTimeInput] = useState<string>(format(new Date(), "HH:mm"))
  const [endDateTime, setEndDateTime] = useState<Date>(new Date(Date.now() + 15 * 60 * 1000))
  const [endTimeInput, setEndTimeInput] = useState<string>(format(new Date(Date.now() + 15 * 60 * 1000), "HH:mm"))
  const [notes, setNotes] = useState("")
  
  // Calculate total duration in minutes
  const totalDurationMinutes = useMemo(() => {
    return Math.round((endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60))
  }, [startDateTime, endDateTime])
  
  // Breast feeding specific
  const [activeSide, setActiveSide] = useState<'left' | 'right' | 'both'>('left')
  const [leftDuration, setLeftDuration] = useState<number>(0)
  const [rightDuration, setRightDuration] = useState<number>(0)
  
  // Remaining time calculation
  const remainingTime = useMemo(() => {
    return Math.max(0, totalDurationMinutes - leftDuration - rightDuration)
  }, [totalDurationMinutes, leftDuration, rightDuration])
  
  // Bottle specific
  const [bottleAmountOz, setBottleAmountOz] = useState<number>(2)
  
  // Formula specific
  const [formulaAmountOz, setFormulaAmountOz] = useState<number>(3)
  const [waterTemp, setWaterTemp] = useState<"room" | "warm">("warm")
  const [formulaBrand, setFormulaBrand] = useState<string>("")
  
  // UI state
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [timeInputsValid, setTimeInputsValid] = useState(false)
  
  // Hooks
  const { toast } = useToast()
  const { selectedChild } = useChildStore()
  const { caregiverId } = useCaregiverStore()
  const queryClient = useQueryClient()

  // Determine the effective child ID to use, prioritizing the explicitly passed childId
  const effectiveChildId = useMemo(() => {
    const id = childId || selectedChild?.id || '';
    
    console.log('FeedingDialog: determining effective child ID', {
      providedChildId: childId,
      storeSelectedChildId: selectedChild?.id,
      effectiveChildId: id
    });
    
    return id;
  }, [childId, selectedChild?.id]);

  // Current selected child object
  const currentChild = useMemo(() => {
    return childId && selectedChild?.id !== childId
      ? { id: childId, name: 'Selected Child' } // This is a placeholder if we only have the ID
      : selectedChild;
  }, [childId, selectedChild]);

  // Get the effective caregiver ID (either from the activity being edited or the current user)
  const effectiveCaregiverId = useMemo(() => {
    if (isEditing && activityToEdit?.caregiver_id) {
      return activityToEdit.caregiver_id;
    }
    return caregiverId;
  }, [isEditing, activityToEdit, caregiverId]);

  // Set whether we're editing on mount and initialize form values based on activity
  useEffect(() => {
    if (activityToEdit) {
      console.log('🔄 INITIALIZING FEEDING DIALOG WITH ACTIVITY', activityToEdit);
      setIsEditing(true);
      
      // For feeding type, validate against allowed values
      const type = activityToEdit.type;
      if (type && ['breastfeeding', 'bottle', 'formula', 'solids'].includes(type)) {
        setFeedingType(type as FeedingTypeEnum);
      } else {
        console.warn(`Invalid feeding type "${type}", defaulting to "breastfeeding"`);
        setFeedingType("breastfeeding");
      }
      
      // Set notes from activity
      if (activityToEdit.notes) {
        setNotes(activityToEdit.notes);
      }
      
      // Set appropriate values based on feeding type
      if (type === "breastfeeding") {
        // Set duration with validation
        const durationValue = typeof activityToEdit.duration === 'number' 
          ? Math.max(1, Math.min(60, activityToEdit.duration)) // Clamp between 1-60
          : 15; // Default
        setLeftDuration(durationValue);
        
        // Set side with validation
        const sideValue = activityToEdit.side === "right" ? "right" : "left";
        setActiveSide(sideValue);
        
        // Set left and right durations
        const leftDurationValue = activityToEdit.left_duration || 0;
        const rightDurationValue = activityToEdit.right_duration || 0;
        setLeftDuration(leftDurationValue);
        setRightDuration(rightDurationValue);
      } else if (type === "bottle") {
        // Set bottle amount with validation
        const amountValue = typeof activityToEdit.amount === 'number'
          ? Math.max(1, Math.min(6, activityToEdit.amount)) // Clamp between 1-6 oz
          : 2; // Default
        setBottleAmountOz(amountValue);
      } else if (type === "formula") {
        // Set formula amount with validation
        const amountValue = typeof activityToEdit.amount === 'number'
          ? Math.max(1, Math.min(6, activityToEdit.amount)) // Clamp between 1-6 oz
          : 3; // Default
        setFormulaAmountOz(amountValue);
        
        // Set water temperature with validation
        const tempValue = activityToEdit.temperature;
        if (tempValue === "room" || tempValue === "warm") {
          setWaterTemp(tempValue);
        } else {
          setWaterTemp("warm"); // Default
        }
        
        // Set formula brand if available
        if (activityToEdit.brand) {
          setFormulaBrand(activityToEdit.brand);
        }
      }
    } else {
      setIsEditing(false);
      // Reset to defaults
      setFeedingType("breastfeeding");
      setLeftDuration(0);
      setRightDuration(0);
    }
  }, [activityToEdit, open]);

  // Update date and time when inputs change
  useEffect(() => {
    const updateDateTime = (dateTime: Date, timeString: string): Date => {
      const [hours, minutes] = timeString.split(':').map(Number)
      const newDate = new Date(dateTime)
      newDate.setHours(hours, minutes)
      return newDate
    }

    setStartDateTime(updateDateTime(startDateTime, startTimeInput))
    setEndDateTime(updateDateTime(endDateTime, endTimeInput))
  }, [startTimeInput, endTimeInput])

  const handleClose = () => {
    onOpenChange(false)
    setShowConfirmation(false)
  }

  // Function to map the component state to a feeding record for database operations
  const createFeedingRecord = (): FeedingSessionInsert => {
    // Validate required fields
    const childIdToUse = effectiveChildId;
    if (!childIdToUse) {
      throw new Error("Child ID is required");
    }
    
    if (!effectiveCaregiverId) {
      throw new Error("Caregiver ID is required");
    }
    
    return {
      child_id: childIdToUse,
      caregiver_id: effectiveCaregiverId,
      type: feedingType,
      start_time: isEditing && activityToEdit?.startTime 
        ? activityToEdit.startTime 
        : formatDateToUTC(startDateTime),
      end_time: isEditing && activityToEdit?.endTime 
        ? activityToEdit.endTime 
        : formatDateToUTC(endDateTime),
      notes: activityToEdit?.notes || ""
    };
  }
  
  // Create feeding-type specific record based on the selected feeding type
  const createSpecificFeedingRecord = (sessionId: string) => {
    if (feedingType === "breastfeeding") {
      return {
        session_id: sessionId,
        feeding_order: [activeSide] as string[],
        left_duration: activeSide === "left" ? leftDuration : rightDuration,
        right_duration: activeSide === "right" ? leftDuration : rightDuration
      } as BreastfeedingSessionInsert;
    }
    
    if (feedingType === "bottle") {
      return {
        session_id: sessionId,
        amount_oz: bottleAmountOz,
        amount_ml: Math.round(bottleAmountOz * 29.5735) // Still need to set amount_ml as it's required
      } as BottleSessionInsert;
    }
    
    if (feedingType === "formula") {
      return {
        session_id: sessionId,
        amount_oz: formulaAmountOz,
        amount_ml: Math.round(formulaAmountOz * 29.5735), // Still need to set amount_ml as it's required
        brand: formulaBrand || "Default Formula"
      } as FormulaSessionInsert;
    }
    
    throw new Error(`Unsupported feeding type: ${feedingType}`);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Only proceed with form submission if confirmation is shown and acknowledged
    if (!showConfirmation) {
      setShowConfirmation(true)
      return
    }
    
    let feedingRecord: FeedingSessionInsert | null = null;
    
    try {
      setSaving(true)
      
      if (!effectiveChildId) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Please select a child first"
        })
        return
      }
      
      if (!effectiveCaregiverId) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Caregiver data not found"
        })
        return
      }
      
      // Create the feeding record using our helper function
      feedingRecord = createFeedingRecord();
      console.log('📝 FEEDING RECORD TO SAVE:', feedingRecord);
      
      let result: FeedingSession;
      
      if (isEditing && activityToEdit?.id) {
        // Update existing record
        const { data, error } = await supabase
          .from('feeding_sessions')
          .update(feedingRecord)
          .eq('id', activityToEdit.id)
          .select();
            
        if (error) throw error;
        if (!data || data.length === 0) {
          throw new Error('No data returned from update operation');
        }
        
        result = data[0];
        
        // Update the specific feeding details based on type
        try {
          if (feedingType === "breastfeeding") {
            const breastfeedingRecord: BreastfeedingSessionUpdate = {
              feeding_order: [activeSide],
              left_duration: activeSide === "left" ? leftDuration : rightDuration,
              right_duration: activeSide === "right" ? leftDuration : rightDuration
            }
            
            const { error: breastfeedingError } = await supabase
              .from('breastfeeding_sessions')
              .update(breastfeedingRecord)
              .eq('session_id', activityToEdit.id);
              
            if (breastfeedingError) {
              console.error('Error updating breastfeeding details:', breastfeedingError);
            }
              
          } else if (feedingType === "bottle") {
            const bottleRecord: BottleSessionUpdate = {
              amount_oz: bottleAmountOz,
              amount_ml: Math.round(bottleAmountOz * 29.5735) // Still need to set amount_ml as it's required
            }
            
            const { error: bottleError } = await supabase
              .from('bottle_sessions')
              .update(bottleRecord)
              .eq('session_id', activityToEdit.id);
              
            if (bottleError) {
              console.error('Error updating bottle details:', bottleError);
            }
              
          } else if (feedingType === "formula") {
            const formulaRecord: FormulaSessionUpdate = {
              amount_oz: formulaAmountOz,
              amount_ml: Math.round(formulaAmountOz * 29.5735), // Still need to set amount_ml as it's required
              brand: formulaBrand || undefined
            }
            
            const { error: formulaError } = await supabase
              .from('formula_sessions')
              .update(formulaRecord)
              .eq('session_id', activityToEdit.id);
              
            if (formulaError) {
              console.error('Error updating formula details:', formulaError);
            }
          }
        } catch (subError) {
          console.error('Error updating specific feeding details:', subError);
        }
          
        toast({
          title: "Feeding record updated",
          description: "Your child's feeding record has been updated successfully."
        })
      } else {
        // Insert new record and get the inserted record
        const { data, error } = await supabase
          .from('feeding_sessions')
          .insert(feedingRecord)
          .select();
        
        if (error) throw error;
        if (!data || data.length === 0) {
          throw new Error('No data returned from insert operation');
        }
        
        result = data[0];
        
        // Now insert the specific feeding details based on type using our helper function
        try {
          const specificFeedingRecord = createSpecificFeedingRecord(result.id);
          
          // Insert the specific record into the appropriate table
          const tableName = feedingType === "breastfeeding" 
            ? 'breastfeeding_sessions' 
            : (feedingType === "bottle" ? 'bottle_sessions' : 'formula_sessions');
            
          const { error: specificError } = await supabase
            .from(tableName)
            .insert(specificFeedingRecord);
            
          if (specificError) {
            console.error(`Error inserting ${feedingType} details:`, specificError);
          }
        } catch (subError) {
          console.error('Error saving specific feeding details:', subError);
          // Consider deleting the parent record if detail insert fails
          // This would prevent orphaned records
        }
        
        toast({
          title: "Feeding recorded",
          description: "Your child's feeding has been logged successfully."
        })
      }
      
      // Invalidate queries to refresh data - using partial key matching to refresh all feeding records queries
      // This will invalidate any query starting with 'feedingRecords' regardless of childId or limit
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && queryKey[0] === 'feedingRecords';
        }
      })
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(result);
      }
      
      // Close the dialog
      handleClose()
    } catch (error) {
      console.error('Error saving feeding record:', error);
      
      // Log more details about the state during error
      console.error('Feeding record state during error:', {
        isEditing,
        activityToEditId: activityToEdit?.id,
        feedingType,
        childId: effectiveChildId,
        effectiveCaregiverId,
        feedingRecord
      });
      
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save feeding record. Please try again."
      })
    } finally {
      setSaving(false)
    }
  }

  // Validate time inputs when they change
  useEffect(() => {
    // Check if start and end times are valid
    const isStartValid = !isNaN(startDateTime.getTime())
    const isEndValid = !isNaN(endDateTime.getTime())
    const isDurationPositive = endDateTime > startDateTime
    
    setTimeInputsValid(isStartValid && isEndValid && isDurationPositive)
    
    // Auto-adjust durations when total time changes
    if (isStartValid && isEndValid && isDurationPositive) {
      const newTotalMinutes = Math.round((endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60))
      
      // If durations exceed new total, adjust them proportionally
      if (leftDuration + rightDuration > newTotalMinutes) {
        const ratio = newTotalMinutes / (leftDuration + rightDuration)
        setLeftDuration(Math.floor(leftDuration * ratio))
        setRightDuration(Math.floor(rightDuration * ratio))
      }
    }
  }, [startDateTime, endDateTime])
  
  // Auto-adjust the other duration when one changes
  useEffect(() => {
    if (activeSide === 'both') {
      // When both sides are active, ensure they don't exceed total time
      if (leftDuration + rightDuration > totalDurationMinutes) {
        if (leftDuration > rightDuration) {
          setLeftDuration(totalDurationMinutes - rightDuration)
        } else {
          setRightDuration(totalDurationMinutes - leftDuration)
        }
      }
    } else if (activeSide === 'left') {
      // When left is active, auto-adjust right duration
      if (leftDuration > totalDurationMinutes) {
        setLeftDuration(totalDurationMinutes)
        setRightDuration(0)
      } else {
        setRightDuration(totalDurationMinutes - leftDuration)
      }
    } else if (activeSide === 'right') {
      // When right is active, auto-adjust left duration
      if (rightDuration > totalDurationMinutes) {
        setRightDuration(totalDurationMinutes)
        setLeftDuration(0)
      } else {
        setLeftDuration(totalDurationMinutes - rightDuration)
      }
    }
  }, [activeSide, leftDuration, rightDuration, totalDurationMinutes])
  
  // Reset current step when dialog opens/closes
  useEffect(() => {
    if (open) {
      setCurrentStep('type')
      setShowConfirmation(false) // Explicitly reset confirmation state when dialog opens
    }
  }, [open])
  
  // Handle step navigation
  const goToNextStep = () => {
    setShowConfirmation(false) // Reset confirmation state when moving between steps
    if (currentStep === 'type') {
      setCurrentStep('time')
    } else if (currentStep === 'time' && timeInputsValid) {
      setCurrentStep('duration')
    }
  }
  
  const goToPrevStep = () => {
    setShowConfirmation(false) // Reset confirmation state when moving between steps
    if (currentStep === 'time') {
      setCurrentStep('type')
    } else if (currentStep === 'duration') {
      setCurrentStep('time')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px] bg-white/5 backdrop-blur-xl border-white/10">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-white">
            {isEditing ? "Edit Feeding Record" : "Record Feeding"}
          </DialogTitle>
          
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className={`h-2 w-2 rounded-full ${currentStep === 'type' ? 'bg-purple-500' : 'bg-white/20'}`}></div>
            <div className={`h-2 w-2 rounded-full ${currentStep === 'time' ? 'bg-purple-500' : 'bg-white/20'}`}></div>
            <div className={`h-2 w-2 rounded-full ${currentStep === 'duration' ? 'bg-purple-500' : 'bg-white/20'}`}></div>
          </div>
          
          <div className="text-center mt-2 text-sm text-gray-400">
            {currentStep === 'type' && 'Step 1: Select Feeding Type'}
            {currentStep === 'time' && 'Step 2: Set Start & End Times'}
            {currentStep === 'duration' && 'Step 3: Allocate Duration'}
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Confirmation Dialog */}
          {showConfirmation && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-card rounded-lg shadow-lg max-w-md w-full p-6 border border-border">
                <h3 className="text-lg font-semibold mb-4 text-white">
                  {isEditing ? "Save Changes?" : "Save Feeding Record?"}
                </h3>
                <p className="mb-6 text-gray-300">
                  {isEditing 
                    ? "You're about to update this feeding record. Please review your changes before confirming."
                    : "You're about to save this feeding record. Please review the details before confirming."
                  }
                </p>
                
                {/* Summary of feeding details */}
                <div className="bg-white/5 p-3 rounded-md mb-4">
                  <div className="text-sm text-gray-300 space-y-2">
                    <div className="flex justify-between">
                      <span>Type:</span>
                      <span className="text-white">{feedingType.charAt(0).toUpperCase() + feedingType.slice(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Start:</span>
                      <span className="text-white">{startDateTime.toLocaleTimeString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>End:</span>
                      <span className="text-white">{endDateTime.toLocaleTimeString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Duration:</span>
                      <span className="text-white">{totalDurationMinutes} minutes</span>
                    </div>
                    {feedingType === "breastfeeding" && (
                      <>
                        <div className="flex justify-between">
                          <span>Left Duration:</span>
                          <span className="text-white">{leftDuration} minutes</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Right Duration:</span>
                          <span className="text-white">{rightDuration} minutes</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Active Side:</span>
                          <span className="text-white capitalize">{activeSide}</span>
                        </div>
                      </>
                    )}
                    {feedingType === "bottle" && (
                      <div className="flex justify-between">
                        <span>Amount:</span>
                        <span className="text-white">{bottleAmountOz} oz ({Math.round(bottleAmountOz * 29.5735)} ml)</span>
                      </div>
                    )}
                    {feedingType === "formula" && (
                      <>
                        <div className="flex justify-between">
                          <span>Amount:</span>
                          <span className="text-white">{formulaAmountOz} oz ({Math.round(formulaAmountOz * 29.5735)} ml)</span>
                        </div>
                        {formulaBrand && (
                          <div className="flex justify-between">
                            <span>Brand:</span>
                            <span className="text-white">{formulaBrand}</span>
                          </div>
                        )}
                      </>
                    )}
                    {notes && (
                      <div className="flex justify-between">
                        <span>Notes:</span>
                        <span className="text-white max-w-[180px] truncate">{notes}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-3 justify-end">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowConfirmation(false)}
                    type="button"
                    className="border-white/10 text-gray-300 hover:bg-white/5"
                  >
                    Back to Edit
                  </Button>
                  <Button 
                    variant="default"
                    onClick={(e) => {
                      e.preventDefault()
                      // Directly call handleSubmit with confirmation already shown
                      const formEvent = new Event('submit', { cancelable: true, bubbles: true }) as unknown as React.FormEvent
                      // Since showConfirmation is already true at this point, this will proceed with the actual submission
                      handleSubmit(formEvent)
                    }}
                    type="button"
                    disabled={saving}
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                  >
                    {saving 
                      ? isEditing ? "Updating..." : "Saving..." 
                      : isEditing ? "Confirm Changes" : "Confirm and Save"
                    }
                  </Button>
                </div>
              </div>
            </div>
          )}
        
          <div className="space-y-4">
            {/* Step 1: Type Selection */}
            {currentStep === 'type' && (
              <>
                <Label className="text-sm font-medium text-gray-400">Select Feeding Type</Label>
                <RadioGroup
                  value={feedingType}
                  onValueChange={(value) => setFeedingType(value as FeedingTypeEnum)}
                  className="grid gap-4"
                >
                  {feedingOptions.map((option) => {
                    const Icon = option.icon
                    return (
                      <label
                        key={option.id}
                        className={cn(
                          "flex items-center space-x-4 rounded-lg border p-4 cursor-pointer transition-colors",
                          "hover:bg-white/5",
                          feedingType === option.id ? "border-purple-500 bg-purple-500/10" : "border-white/10"
                        )}
                      >
                        <RadioGroupItem value={option.id} id={option.id} className="sr-only" />
                        <div className={cn(
                          "p-2 rounded-lg",
                          feedingType === option.id ? "bg-purple-500/20" : "bg-white/5"
                        )}>
                          <Icon className={cn(
                            "w-5 h-5",
                            feedingType === option.id ? "text-purple-300" : "text-gray-400"
                          )} />
                        </div>
                        <div className="flex-1">
                          <p className={cn(
                            "font-medium",
                            feedingType === option.id ? "text-white" : "text-gray-300"
                          )}>{option.label}</p>
                          <p className="text-sm text-gray-400">{option.description}</p>
                        </div>
                      </label>
                    )
                  })}
                </RadioGroup>
              </>
            )}
          </div>

          {/* Step 2: Time Selection */}
          {currentStep === 'time' && (
            <div className="space-y-4">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-center mb-2 text-white">
                  When did the feeding start?
                </h3>
                <div className="flex flex-col gap-4 max-w-sm mx-auto">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className="w-full justify-start text-left font-normal h-auto py-3 border-white/10 text-gray-200"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(startDateTime, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="center">
                      <Calendar
                        mode="single"
                        selected={startDateTime}
                        onSelect={(date) => {
                          if (date) {
                            // Preserve the time from the existing date
                            const newDate = new Date(date)
                            newDate.setHours(startDateTime.getHours(), startDateTime.getMinutes())
                            setStartDateTime(newDate)
                          }
                        }}
                        disabled={(date) => date > new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      className="flex-1 bg-white/5 border-white/10 text-gray-200"
                      value={startTimeInput}
                      onChange={(e) => {
                        setStartTimeInput(e.target.value)
                      }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-center mb-2 text-white">
                  When did the feeding end?
                </h3>
                <div className="flex flex-col gap-4 max-w-sm mx-auto">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className="w-full justify-start text-left font-normal h-auto py-3 border-white/10 text-gray-200"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(endDateTime, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="center">
                      <Calendar
                        mode="single"
                        selected={endDateTime}
                        onSelect={(date) => {
                          if (date) {
                            // Preserve the time from the existing date
                            const newDate = new Date(date)
                            newDate.setHours(endDateTime.getHours(), endDateTime.getMinutes())
                            setEndDateTime(newDate)
                          }
                        }}
                        disabled={(date) => date > new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      className="flex-1 bg-white/5 border-white/10 text-gray-200"
                      value={endTimeInput}
                      onChange={(e) => {
                        setEndTimeInput(e.target.value)
                      }}
                    />
                  </div>
                </div>
              </div>
              
              {!timeInputsValid && (
                <div className="text-red-400 text-sm mt-2 text-center">
                  Please ensure end time is after start time.
                </div>
              )}
              
              {timeInputsValid && (
                <div className="text-green-400 text-sm mt-2 text-center">
                  Total duration: {totalDurationMinutes} minutes
                </div>
              )}
            </div>
          )}

          {/* Step 3: Duration Allocation */}
          {currentStep === 'duration' && (
            <div className="space-y-6">
              {/* Breastfeeding specific fields */}
              {feedingType === 'breastfeeding' && (
                <>
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-center text-white">Select Active Side</h3>
                    <div className="flex justify-center gap-4 p-2">
                      <Button
                        type="button"
                        onClick={() => setActiveSide('left')}
                        className={cn(
                          "flex-1 py-6 rounded-md flex flex-col items-center justify-center gap-2",
                          activeSide === 'left' 
                            ? "bg-purple-500/80 text-white" 
                            : "bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10"
                        )}
                      >
                        <div className="font-semibold text-lg mb-1">Left</div>
                        <div className={cn(
                          "w-12 h-12 rounded-full flex items-center justify-center",
                          activeSide === 'left' ? "bg-white/20" : "bg-white/5"
                        )}>L</div>
                        <div className="text-sm">{leftDuration} min</div>
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setActiveSide('right')}
                        className={cn(
                          "flex-1 py-6 rounded-md flex flex-col items-center justify-center gap-2",
                          activeSide === 'right' 
                            ? "bg-purple-500/80 text-white" 
                            : "bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10"
                        )}
                      >
                        <div className="font-semibold text-lg mb-1">Right</div>
                        <div className={cn(
                          "w-12 h-12 rounded-full flex items-center justify-center",
                          activeSide === 'right' ? "bg-white/20" : "bg-white/5"
                        )}>R</div>
                        <div className="text-sm">{rightDuration} min</div>
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setActiveSide('both')}
                        className={cn(
                          "flex-1 py-6 rounded-md flex flex-col items-center justify-center gap-2",
                          activeSide === 'both' 
                            ? "bg-purple-500/80 text-white" 
                            : "bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10"
                        )}
                      >
                        <div className="font-semibold text-lg mb-1">Both</div>
                        <div className={cn(
                          "w-12 h-12 rounded-full flex items-center justify-center",
                          activeSide === 'both' ? "bg-white/20" : "bg-white/5"
                        )}>B</div>
                        <div className="text-sm">Dual</div>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 pt-4">
                    <div className="flex justify-between">
                      <h3 className="font-medium text-white">Left Duration: {leftDuration} min</h3>
                      {activeSide !== 'left' && (
                        <button 
                          type="button" 
                          onClick={() => setActiveSide('left')}
                          className="text-xs text-purple-400 hover:text-purple-300"
                        >
                          Set Active
                        </button>
                      )}
                    </div>
                    <Slider
                      value={[leftDuration]}
                      min={0}
                      max={totalDurationMinutes}
                      step={1}
                      onValueChange={(value) => setLeftDuration(value[0])}
                      className="py-4"
                    />
                    <div className="text-xs text-gray-400 mt-1 flex justify-between">
                      <span>0 min</span>
                      <span>{totalDurationMinutes} min</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2 pt-2">
                    <div className="flex justify-between">
                      <h3 className="font-medium text-white">Right Duration: {rightDuration} min</h3>
                      {activeSide !== 'right' && (
                        <button 
                          type="button" 
                          onClick={() => setActiveSide('right')}
                          className="text-xs text-purple-400 hover:text-purple-300"
                        >
                          Set Active
                        </button>
                      )}
                    </div>
                    <Slider
                      value={[rightDuration]}
                      min={0}
                      max={totalDurationMinutes}
                      step={1}
                      onValueChange={(value) => setRightDuration(value[0])}
                      className="py-4"
                    />
                    <div className="text-xs text-gray-400 mt-1 flex justify-between">
                      <span>0 min</span>
                      <span>{totalDurationMinutes} min</span>
                    </div>
                  </div>
                  
                  {remainingTime > 0 && (
                    <div className="text-yellow-400 text-sm text-center mt-3">
                      <Clock className="inline-block mr-1 h-4 w-4" />
                      {remainingTime} minutes unallocated
                    </div>
                  )}
                </>
              )}
              
              {/* Bottle feeding specific fields */}
              {feedingType === 'bottle' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-center text-white mb-6">Amount Fed</h3>
                  
                  <div className="flex flex-col items-center">
                    <div className="bg-white/5 p-6 rounded-2xl mb-6 relative flex items-center justify-center">
                      <Milk className="h-16 w-16 text-blue-300 opacity-60" />
                      <div className="absolute inset-x-0 bottom-0 h-[60%] bg-blue-400/20 rounded-b-2xl rounded-t-3xl" 
                           style={{ height: `${Math.min(100, bottleAmountOz * 16)}%` }}></div>
                      <div className="relative z-10 text-5xl font-bold text-white">{bottleAmountOz}</div>
                      <div className="relative z-10 text-lg ml-1 self-end mb-2 text-blue-200">oz</div>
                    </div>
                    
                    <div className="w-full max-w-md space-y-4 mt-2">
                      <div className="flex items-center gap-4">
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          disabled={bottleAmountOz <= 0.5}
                          onClick={() => setBottleAmountOz(Math.max(0.5, bottleAmountOz - 0.5))}
                          className="h-10 w-10 rounded-full"
                        >
                          <span className="text-lg font-bold">-</span>
                        </Button>
                        
                        <Slider
                          value={[bottleAmountOz]}
                          min={0.5}
                          max={8}
                          step={0.5}
                          onValueChange={(value) => setBottleAmountOz(value[0])}
                          className="flex-1"
                        />
                        
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="icon"
                          disabled={bottleAmountOz >= 8}
                          onClick={() => setBottleAmountOz(Math.min(8, bottleAmountOz + 0.5))}
                          className="h-10 w-10 rounded-full"
                        >
                          <span className="text-lg font-bold">+</span>
                        </Button>
                      </div>
                      
                      <div className="text-xs text-gray-400 mt-1 flex justify-between">
                        <span>0.5 oz</span>
                        <span>8 oz</span>
                      </div>
                      
                      <div className="text-center text-sm text-blue-200 mt-2">
                        Approximately {Math.round(bottleAmountOz * 29.5735)} ml
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Formula feeding specific fields */}
              {feedingType === 'formula' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-center text-white mb-2">Formula Details</h3>
                  
                  <div className="space-y-6">
                    <div className="flex flex-col items-center">
                      <div className="bg-white/5 p-6 rounded-2xl mb-4 relative flex items-center justify-center">
                        <DropletIcon className="h-16 w-16 text-yellow-200 opacity-60" />
                        <div className="absolute inset-x-0 bottom-0 h-[60%] bg-yellow-400/20 rounded-b-2xl rounded-t-3xl" 
                             style={{ height: `${Math.min(100, formulaAmountOz * 16)}%` }}></div>
                        <div className="relative z-10 text-5xl font-bold text-white">{formulaAmountOz}</div>
                        <div className="relative z-10 text-lg ml-1 self-end mb-2 text-yellow-200">oz</div>
                      </div>
                      
                      <div className="w-full max-w-md space-y-2">
                        <div className="flex items-center gap-4">
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="icon"
                            disabled={formulaAmountOz <= 0.5}
                            onClick={() => setFormulaAmountOz(Math.max(0.5, formulaAmountOz - 0.5))}
                            className="h-10 w-10 rounded-full"
                          >
                            <span className="text-lg font-bold">-</span>
                          </Button>
                          
                          <Slider
                            value={[formulaAmountOz]}
                            min={0.5}
                            max={8}
                            step={0.5}
                            onValueChange={(value) => setFormulaAmountOz(value[0])}
                            className="flex-1"
                          />
                          
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="icon"
                            disabled={formulaAmountOz >= 8}
                            onClick={() => setFormulaAmountOz(Math.min(8, formulaAmountOz + 0.5))}
                            className="h-10 w-10 rounded-full"
                          >
                            <span className="text-lg font-bold">+</span>
                          </Button>
                        </div>
                        
                        <div className="text-xs text-gray-400 mt-1 flex justify-between">
                          <span>0.5 oz</span>
                          <span>8 oz</span>
                        </div>
                        
                        <div className="text-center text-sm text-yellow-200 mt-2">
                          Approximately {Math.round(formulaAmountOz * 29.5735)} ml
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3 pt-4">
                      <Label className="text-sm font-medium text-gray-300">Formula Brand (Optional)</Label>
                      <Input
                        value={formulaBrand}
                        onChange={(e) => setFormulaBrand(e.target.value)}
                        placeholder="E.g., Similac, Enfamil, etc."
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    
                    <div className="space-y-3 pt-2">
                      <Label className="text-sm font-medium text-gray-300">Water Temperature</Label>
                      <div className="flex justify-center gap-4">
                        <Button
                          type="button"
                          onClick={() => setWaterTemp('room')}
                          className={cn(
                            "flex-1 py-4 rounded-md flex flex-col items-center justify-center gap-2",
                            waterTemp === 'room' 
                              ? "bg-yellow-500/50 text-white" 
                              : "bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10"
                          )}
                        >
                          <ThermometerIcon className={cn(
                            "h-6 w-6 mb-1",
                            waterTemp === 'room' ? "text-white" : "text-gray-400"
                          )} />
                          <div className="font-medium">Room Temp</div>
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setWaterTemp('warm')}
                          className={cn(
                            "flex-1 py-4 rounded-md flex flex-col items-center justify-center gap-2",
                            waterTemp === 'warm' 
                              ? "bg-yellow-500/50 text-white" 
                              : "bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10"
                          )}
                        >
                          <ThermometerIcon className={cn(
                            "h-6 w-6 mb-1",
                            waterTemp === 'warm' ? "text-white" : "text-gray-400"
                          )} />
                          <div className="font-medium">Warm</div>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Notes field - shared across all feeding types */}
              <div className="space-y-3 pt-6 border-t border-white/10">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-gray-400" />
                  <Label className="text-sm font-medium text-gray-300">Notes (Optional)</Label>
                </div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any additional details about this feeding session..."
                  className="w-full h-24 p-3 rounded-md bg-white/5 border border-white/10 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          )}
        
          {/* Navigation buttons */}
          <div className="flex gap-3 mt-8">
            {currentStep === 'type' ? (
              // Only show next button for type selection
              <Button 
                type="button" 
                onClick={goToNextStep}
                className="w-full py-6 text-lg font-medium rounded-lg bg-purple-600 hover:bg-purple-700 transition-colors"
              >
                Continue
              </Button>
            ) : (
              // For other steps show navigation buttons
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={goToPrevStep}
                  className="flex-1 py-5 border-white/10 hover:bg-white/5"
                >
                  Back
                </Button>
                {currentStep === 'time' ? (
                  <Button 
                    type="button" 
                    onClick={goToNextStep}
                    disabled={!timeInputsValid}
                    className="flex-1 py-5 bg-purple-600 hover:bg-purple-700 transition-colors"
                  >
                    Next
                  </Button>
                ) : ( // Last step
                  <Button 
                    type="button"
                    onClick={() => setShowConfirmation(true)}
                    className="flex-1 py-5 bg-purple-600 hover:bg-purple-700 transition-colors"
                  >
                    {isEditing ? "Save Changes" : "Save Feeding"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
